import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { UtensilsCrossed, Plus, Trash2, Sparkles, Zap, Baby, Check, X, Edit2, Loader2, RefreshCw, Camera, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const mealTypes = ['breakfast', 'lunch', 'dinner'];

const MealPlannerPage = () => {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  
  // HITL State for AI suggestions
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiCallId, setAiCallId] = useState(null);
  const [editedSuggestions, setEditedSuggestions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [acceptedDays, setAcceptedDays] = useState([]); // Priority 3: Track accepted individual days
  const [regeneratingDay, setRegeneratingDay] = useState(null); // Priority 3: Track which day is regenerating

  // E7 Task A: Fridge Photo Scan
  const [fridgeImage, setFridgeImage] = useState(null);
  const [fridgeScanning, setFridgeScanning] = useState(false);
  const [fridgeResult, setFridgeResult] = useState(null);
  const [fridgeCallId, setFridgeCallId] = useState(null);
  const [fridgeSlots, setFridgeSlots] = useState({});
  const [savedFridgeMeals, setSavedFridgeMeals] = useState([]);
  const [addingStaples, setAddingStaples] = useState(false);
  
  const [newMeal, setNewMeal] = useState({
    day: 'monday',
    meal_type: 'dinner',
    name: '',
    prep_notes: '',
    is_kid_friendly: true,
    is_quick: false
  });

  // HITL confirmation helper
  const confirmAIAction = async (callId, userAction, editDelta = []) => {
    if (!callId) return;
    try {
      await axios.post(`${API}/ai/confirm`, {
        call_id: callId,
        user_action: userAction,
        edit_delta: editDelta
      });
    } catch (error) {
      console.error('Failed to confirm AI action:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (aiCallId && aiSuggestion) {
        confirmAIAction(aiCallId, 'ignored');
      }
    };
  }, [aiCallId, aiSuggestion]);

  useEffect(() => {
    fetchMeals();
  }, []);

  const fetchMeals = async () => {
    try {
      const response = await axios.get(`${API}/meals`);
      setMeals(response.data);
    } catch (error) {
      console.error('Error fetching meals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/meals`, newMeal);
      toast.success('Meal added!');
      setDialogOpen(false);
      setNewMeal({ day: 'monday', meal_type: 'dinner', name: '', prep_notes: '', is_kid_friendly: true, is_quick: false });
      fetchMeals();
    } catch (error) {
      toast.error('Failed to add meal');
    }
  };

  const deleteMeal = async (id) => {
    try {
      await axios.delete(`${API}/meals/${id}`);
      toast.success('Meal removed');
      fetchMeals();
    } catch (error) {
      toast.error('Failed to delete meal');
    }
  };

  const getAiSuggestions = async () => {
    setAiLoading(true);
    setAiCallId(null);
    try {
      const response = await axios.post(`${API}/ai/meal-suggestion`, {
        preferences: 'family-friendly, easy to prepare',
        existing_meals: meals.map(m => m.name)
      });
      const suggestion = response.data.suggestion;
      setAiSuggestion(suggestion);
      setAiCallId(response.data.call_id || null);
      // Initialize editable copy
      if (suggestion?.suggestions) {
        setEditedSuggestions(JSON.parse(JSON.stringify(suggestion.suggestions)));
      }
      setIsEditing(false);
    } catch (error) {
      toast.error('AI suggestions unavailable');
    } finally {
      setAiLoading(false);
    }
  };

  // HITL: Update a suggestion field
  const updateSuggestion = (index, field, value) => {
    const updated = [...editedSuggestions];
    updated[index] = { ...updated[index], [field]: value };
    setEditedSuggestions(updated);
    setIsEditing(true);
  };

  // HITL: Calculate edit delta
  const calculateEditDelta = () => {
    const delta = [];
    if (!aiSuggestion?.suggestions) return delta;
    editedSuggestions.forEach((edited, index) => {
      const original = aiSuggestion.suggestions[index];
      if (!original) return;
      ['day', 'meal_type', 'name', 'prep_time'].forEach(field => {
        if (edited[field] !== original[field]) {
          delta.push({
            field: `meal_${index}_${field}`,
            original: String(original[field] || ''),
            corrected: String(edited[field] || '')
          });
        }
      });
    });
    return delta;
  };

  // HITL: Save all AI suggestions to meals
  const handleUseSuggestions = async () => {
    const editDelta = calculateEditDelta();
    const userAction = editDelta.length > 0 ? 'edited' : 'accepted';
    
    await confirmAIAction(aiCallId, userAction, editDelta);
    
    // Save each meal suggestion
    for (const suggestion of editedSuggestions) {
      try {
        await axios.post(`${API}/meals`, {
          day: suggestion.day?.toLowerCase() || 'monday',
          meal_type: suggestion.meal_type || 'dinner',
          name: suggestion.name,
          prep_notes: suggestion.prep_time ? `Prep time: ${suggestion.prep_time}` : '',
          is_kid_friendly: suggestion.is_kid_friendly ?? true,
          is_quick: suggestion.prep_time?.includes('15') || suggestion.prep_time?.includes('20') || false
        });
      } catch (error) {
        console.error('Failed to save meal:', error);
      }
    }
    
    toast.success(`${editedSuggestions.length} meals added to your plan!`);
    setAiSuggestion(null);
    setAiCallId(null);
    setEditedSuggestions([]);
    setIsEditing(false);
    fetchMeals();
  };

  // HITL: Discard AI suggestions
  const handleDiscardSuggestions = async () => {
    await confirmAIAction(aiCallId, 'ignored');
    setAiSuggestion(null);
    setAiCallId(null);
    setEditedSuggestions([]);
    setIsEditing(false);
    setAcceptedDays([]);
    toast.info('AI suggestions discarded');
  };

  // Priority 3: Accept individual day's meals
  const handleAcceptDay = async (dayName) => {
    const dayMeals = editedSuggestions.filter(s => s.day?.toLowerCase() === dayName.toLowerCase());
    if (dayMeals.length === 0) return;
    
    // Save meals for this day
    for (const suggestion of dayMeals) {
      try {
        await axios.post(`${API}/meals`, {
          day: suggestion.day?.toLowerCase() || dayName,
          meal_type: suggestion.meal_type || 'dinner',
          name: suggestion.name,
          prep_notes: suggestion.prep_time ? `Prep time: ${suggestion.prep_time}` : '',
          is_kid_friendly: suggestion.is_kid_friendly ?? true,
          is_quick: suggestion.prep_time?.includes('15') || suggestion.prep_time?.includes('20') || false
        });
      } catch (error) {
        console.error('Failed to save meal:', error);
      }
    }
    
    // Mark day as accepted
    setAcceptedDays(prev => [...prev, dayName.toLowerCase()]);
    toast.success(`${dayName}'s meals added!`);
    fetchMeals();
    
    // Check if all days are now accepted
    const allDaysAccepted = [...acceptedDays, dayName.toLowerCase()];
    const uniqueDays = [...new Set(editedSuggestions.map(s => s.day?.toLowerCase()))];
    if (uniqueDays.every(d => allDaysAccepted.includes(d))) {
      await confirmAIAction(aiCallId, 'accepted');
      setAiSuggestion(null);
      setAiCallId(null);
      setEditedSuggestions([]);
      setAcceptedDays([]);
    }
  };

  // Priority 3: Regenerate suggestions for a specific day
  const handleRegenerateDay = async (dayName) => {
    setRegeneratingDay(dayName);
    try {
      const response = await axios.post(`${API}/ai/meal-suggestion`, {
        preferences: 'family-friendly meals',
        existing_meals: [],
        specific_day: dayName
      });
      
      if (response.data.suggestion?.suggestions) {
        // Replace this day's suggestions with new ones
        const otherDays = editedSuggestions.filter(s => s.day?.toLowerCase() !== dayName.toLowerCase());
        const newDaySuggestions = response.data.suggestion.suggestions;
        setEditedSuggestions([...otherDays, ...newDaySuggestions]);
        toast.success(`New suggestions for ${dayName}!`);
      }
    } catch (error) {
      toast.error('Failed to regenerate');
    } finally {
      setRegeneratingDay(null);
    }
  };

  // E7 Task A: Fridge Photo Scan - pick a photo
  const handleFridgeSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose a photo of your fridge or pantry');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setFridgeImage(reader.result);
      setFridgeResult(null);
      setFridgeCallId(null);
      setSavedFridgeMeals([]);
    };
    reader.readAsDataURL(file);
  };

  const runFridgeScan = async () => {
    if (!fridgeImage) {
      toast.error('Add a fridge photo first');
      return;
    }
    setFridgeScanning(true);
    try {
      const response = await axios.post(`${API}/ai/meals/fridge-scan`, {
        image_base64: fridgeImage,
        preferences: 'family-friendly, quick weeknight meals'
      });
      const result = response.data.scan_result || {};
      if (result.error) {
        toast.error(result.error);
        return;
      }
      setFridgeResult(result);
      setFridgeCallId(response.data.call_id || null);
      setFridgeSlots({});
      setSavedFridgeMeals([]);
      const count = result.meal_suggestions?.length || 0;
      toast.success(count ? `${count} meal ideas from your fridge` : 'No meals could be suggested from that photo');
    } catch (error) {
      toast.error('Could not read that photo right now');
    } finally {
      setFridgeScanning(false);
    }
  };

  // HITL: accept one fridge meal into the plan
  const addFridgeMeal = async (suggestion, index) => {
    const slot = fridgeSlots[index] || {};
    try {
      await axios.post(`${API}/meals`, {
        day: slot.day || 'monday',
        meal_type: slot.meal_type || 'dinner',
        name: suggestion.name,
        prep_notes: [suggestion.prep_time ? `Prep time: ${suggestion.prep_time}` : '', suggestion.instructions || '']
          .filter(Boolean).join(' - '),
        is_kid_friendly: true,
        is_quick: /10|15|20/.test(suggestion.prep_time || '')
      });
      setSavedFridgeMeals(prev => [...prev, index]);
      toast.success(`${suggestion.name} added to ${slot.day || 'monday'}`);
      fetchMeals();
    } catch (error) {
      toast.error('Failed to add meal');
    }
  };

  const addMissingStaples = async () => {
    const staples = fridgeResult?.missing_staples || [];
    if (staples.length === 0) return;
    setAddingStaples(true);
    try {
      for (const item of staples) {
        await axios.post(`${API}/groceries`, {
          name: typeof item === 'string' ? item : item?.name || 'Item',
          category: 'other',
          restock_needed: true
        });
      }
      toast.success(`${staples.length} items added to your grocery list`);
    } catch (error) {
      toast.error('Could not add to grocery list');
    } finally {
      setAddingStaples(false);
    }
  };

  const finishFridgeScan = async (action) => {
    await confirmAIAction(fridgeCallId, action);
    setFridgeResult(null);
    setFridgeCallId(null);
    setFridgeImage(null);
    setFridgeSlots({});
    setSavedFridgeMeals([]);
    if (action === 'ignored') toast.info('Fridge suggestions dismissed');
  };

  const getMealsByDay = (day) => {
    return meals.filter(m => m.day === day);
  };  const getMealTypeColor = (type) => {
    switch (type) {
      case 'breakfast': return 'bg-yellow-100 text-yellow-700';
      case 'lunch': return 'bg-green-100 text-green-700';
      case 'dinner': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6" data-testid="meal-planner-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-nurture-navy flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <UtensilsCrossed className="w-5 h-5 text-orange-600" />
            </div>
            Meal Planner
          </h1>
          <p className="text-nurture-muted mt-1">Plan your family's meals for the week</p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={getAiSuggestions}
            disabled={aiLoading}
            variant="outline"
            className="rounded-full"
            data-testid="ai-meal-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {aiLoading ? 'Thinking...' : 'AI Ideas'}
          </Button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-nurture-terracotta hover:bg-nurture-terracotta-hover text-white rounded-full shadow-nurture-button" data-testid="add-meal-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Meal
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader>
                <DialogTitle className="font-heading">Add Meal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Day</Label>
                    <Select value={newMeal.day} onValueChange={(v) => setNewMeal({ ...newMeal, day: v })}>
                      <SelectTrigger className="rounded-xl capitalize" data-testid="meal-day">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {days.map(day => (
                          <SelectItem key={day} value={day} className="capitalize">{day}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Meal Type</Label>
                    <Select value={newMeal.meal_type} onValueChange={(v) => setNewMeal({ ...newMeal, meal_type: v })}>
                      <SelectTrigger className="rounded-xl capitalize" data-testid="meal-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {mealTypes.map(type => (
                          <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Meal Name</Label>
                  <Input
                    value={newMeal.name}
                    onChange={(e) => setNewMeal({ ...newMeal, name: e.target.value })}
                    placeholder="Spaghetti Bolognese"
                    required
                    className="rounded-xl"
                    data-testid="meal-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Prep Notes</Label>
                  <Textarea
                    value={newMeal.prep_notes}
                    onChange={(e) => setNewMeal({ ...newMeal, prep_notes: e.target.value })}
                    placeholder="Defrost meat night before..."
                    className="rounded-xl"
                    data-testid="meal-notes"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newMeal.is_kid_friendly}
                      onCheckedChange={(v) => setNewMeal({ ...newMeal, is_kid_friendly: v })}
                      data-testid="meal-kid-friendly"
                    />
                    <Label className="flex items-center gap-1">
                      <Baby className="w-4 h-4" />
                      Kid Friendly
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newMeal.is_quick}
                      onCheckedChange={(v) => setNewMeal({ ...newMeal, is_quick: v })}
                      data-testid="meal-quick"
                    />
                    <Label className="flex items-center gap-1">
                      <Zap className="w-4 h-4" />
                      Quick Meal
                    </Label>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-nurture-terracotta hover:bg-nurture-terracotta-hover rounded-full" data-testid="meal-submit">
                  Add Meal
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* AI Suggestion with HITL */}
      {aiSuggestion && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-none rounded-3xl">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-bold text-nurture-navy">AI Meal Suggestions</h3>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={isEditing ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>
                        {isEditing ? 'Edited' : 'AI Generated'}
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Editable Suggestions - Grouped by Day with Per-Day Actions (Priority 3) */}
                  {editedSuggestions.length > 0 && (
                    <div className="space-y-4 mb-4">
                      {/* Get unique days */}
                      {[...new Set(editedSuggestions.map(s => s.day?.toLowerCase()))].map(dayName => {
                        const dayMeals = editedSuggestions.filter(s => s.day?.toLowerCase() === dayName);
                        const isDayAccepted = acceptedDays.includes(dayName);
                        const isRegenerating = regeneratingDay === dayName;
                        
                        return (
                          <div key={dayName} className={`p-4 rounded-xl border ${isDayAccepted ? 'bg-green-50 border-green-200' : 'bg-white/70 border-orange-200'}`}>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium text-nurture-navy capitalize flex items-center gap-2">
                                {dayName}
                                {isDayAccepted && <Check className="w-4 h-4 text-green-600" />}
                              </h4>
                              {!isDayAccepted && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRegenerateDay(dayName)}
                                    disabled={isRegenerating}
                                    className="h-7 text-xs"
                                    data-testid={`regenerate-${dayName}-btn`}
                                  >
                                    {isRegenerating ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <RefreshCw className="w-3 h-3 mr-1" />
                                    )}
                                    {isRegenerating ? '' : 'Regenerate'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleAcceptDay(dayName)}
                                    className="h-7 text-xs bg-green-500 hover:bg-green-600"
                                    data-testid={`accept-${dayName}-btn`}
                                  >
                                    <Check className="w-3 h-3 mr-1" />
                                    Accept
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="space-y-2">
                              {dayMeals.map((suggestion, index) => {
                                const globalIndex = editedSuggestions.indexOf(suggestion);
                                return (
                                  <div key={index} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
                                    <Badge variant="outline" className="w-fit text-xs">
                                      {suggestion.meal_type || 'meal'}
                                    </Badge>
                                    <Input
                                      value={suggestion.name || ''}
                                      onChange={(e) => updateSuggestion(globalIndex, 'name', e.target.value)}
                                      placeholder="Meal name"
                                      className="sm:col-span-2 font-medium text-sm"
                                      disabled={isDayAccepted}
                                      data-testid={`meal-suggestion-name-${globalIndex}`}
                                    />
                                    {suggestion.prep_time && (
                                      <p className="text-xs text-nurture-muted col-span-3">Prep: {suggestion.prep_time}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Shopping tip */}
                  {aiSuggestion.shopping_tip && (
                    <p className="text-sm text-nurture-muted mb-4 italic">
                      💡 {aiSuggestion.shopping_tip}
                    </p>
                  )}
                  
                  {/* HITL Action Buttons */}
                  <div className="flex items-center gap-2 pt-2 border-t border-orange-200">
                    <Button
                      onClick={handleUseSuggestions}
                      className="bg-nurture-sage hover:bg-nurture-sage-hover text-white"
                      data-testid="use-meal-suggestions-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {isEditing ? 'Save Edited Meals' : 'Use All Suggestions'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDiscardSuggestions}
                      data-testid="discard-meal-suggestions-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Discard
                    </Button>
                    <span className="text-xs text-nurture-muted ml-auto">
                      Edit fields above before saving
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* E7 Task A: Fridge Photo Scan */}
      <Card className="rounded-3xl border-2 border-dashed border-orange-200 bg-gradient-to-br from-orange-50/60 to-amber-50/40" data-testid="fridge-scan-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-nurture-navy flex items-center gap-2 text-base md:text-lg">
            <Camera className="w-5 h-5 text-orange-600" />
            What's in the fridge?
            <Badge className="bg-orange-100 text-orange-700">AI</Badge>
          </CardTitle>
          <p className="text-sm text-nurture-muted">
            Snap a photo of your fridge or pantry and get meals you can actually cook tonight.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFridgeSelect}
                data-testid="fridge-photo-input"
              />
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-orange-300 text-nurture-navy bg-white hover:bg-orange-50 transition-colors text-sm">
                <Camera className="w-4 h-4" />
                {fridgeImage ? 'Change photo' : 'Choose photo'}
              </span>
            </label>
            <Button
              onClick={runFridgeScan}
              disabled={!fridgeImage || fridgeScanning}
              className="rounded-full bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="fridge-scan-btn"
            >
              {fridgeScanning ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reading your fridge...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Scan for meal ideas</>
              )}
            </Button>
          </div>

          {fridgeImage && (
            <img
              src={fridgeImage}
              alt="Fridge preview"
              className="w-full max-w-xs rounded-2xl object-cover max-h-48"
              data-testid="fridge-preview"
            />
          )}

          {fridgeResult && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4 pt-2 border-t border-orange-200"
              data-testid="fridge-result"
            >
              {fridgeResult.detected_ingredients?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-nurture-navy mb-2">I spotted</p>
                  <div className="flex flex-wrap gap-2" data-testid="fridge-ingredients">
                    {fridgeResult.detected_ingredients.map((ing, i) => (
                      <Badge key={i} variant="outline" className="text-xs bg-white">{ing}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {(fridgeResult.meal_suggestions || []).map((s, index) => (
                  <div key={index} className="p-4 rounded-2xl bg-white border border-orange-100" data-testid={`fridge-suggestion-${index}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="font-medium text-nurture-navy">{s.name}</p>
                        {s.prep_time && <p className="text-xs text-nurture-muted mt-0.5">{s.prep_time}</p>}
                        {s.uses_ingredients?.length > 0 && (
                          <p className="text-xs text-nurture-muted mt-1">Uses: {s.uses_ingredients.join(', ')}</p>
                        )}
                        {s.instructions && <p className="text-sm text-nurture-navy/70 mt-2">{s.instructions}</p>}
                      </div>
                      {savedFridgeMeals.includes(index) ? (
                        <Badge className="bg-green-100 text-green-700" data-testid={`fridge-saved-${index}`}>
                          <Check className="w-3 h-3 mr-1" />Added
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Select
                            value={fridgeSlots[index]?.day || 'monday'}
                            onValueChange={(v) => setFridgeSlots(prev => ({ ...prev, [index]: { ...prev[index], day: v } }))}
                          >
                            <SelectTrigger className="h-8 w-[120px] rounded-xl capitalize text-xs" data-testid={`fridge-day-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {days.map(d => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Select
                            value={fridgeSlots[index]?.meal_type || 'dinner'}
                            onValueChange={(v) => setFridgeSlots(prev => ({ ...prev, [index]: { ...prev[index], meal_type: v } }))}
                          >
                            <SelectTrigger className="h-8 w-[110px] rounded-xl capitalize text-xs" data-testid={`fridge-type-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {mealTypes.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            onClick={() => addFridgeMeal(s, index)}
                            className="rounded-full bg-nurture-sage hover:bg-nurture-sage-hover text-white"
                            data-testid={`fridge-add-${index}`}
                          >
                            <Plus className="w-4 h-4 mr-1" />Add
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {fridgeResult.missing_staples?.length > 0 && (
                <div className="p-4 rounded-2xl bg-white border border-orange-100">
                  <p className="text-sm font-medium text-nurture-navy">You'd still need</p>
                  <p className="text-sm text-nurture-muted mt-1">{fridgeResult.missing_staples.join(', ')}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addMissingStaples}
                    disabled={addingStaples}
                    className="rounded-full mt-3"
                    data-testid="fridge-add-staples-btn"
                  >
                    {addingStaples ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
                    Add to grocery list
                  </Button>
                </div>
              )}

              {fridgeResult.tip && (
                <p className="text-sm italic text-nurture-muted">{fridgeResult.tip}</p>
              )}

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => finishFridgeScan('accepted')}
                  className="rounded-full"
                  data-testid="fridge-done-btn"
                >
                  <Check className="w-4 h-4 mr-2" />Done
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => finishFridgeScan('ignored')}
                  className="rounded-full text-nurture-muted"
                  data-testid="fridge-dismiss-btn"
                >
                  <X className="w-4 h-4 mr-2" />Dismiss
                </Button>
              </div>
            </motion.div>
          )}
        </CardContent>
      </Card>

      {/* Weekly Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7].map(i => (
            <div key={i} className="h-64 bg-nurture-cream-dark rounded-3xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {days.map((day, index) => {
            const dayMeals = getMealsByDay(day);
            return (
              <motion.div
                key={day}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="rounded-3xl shadow-nurture h-full">
                  <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-nurture-navy capitalize">{day}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dayMeals.length === 0 ? (
                      <div className="py-8 text-center text-nurture-muted">
                        <UtensilsCrossed className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No meals planned</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {dayMeals.map((meal) => (
                          <div
                            key={meal.id}
                            className="p-3 rounded-xl bg-nurture-cream/50 group relative"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <Badge className={`text-xs mb-1 ${getMealTypeColor(meal.meal_type)}`}>
                                  {meal.meal_type}
                                </Badge>
                                <p className="font-medium text-nurture-navy">{meal.name}</p>
                                <div className="flex gap-1 mt-1">
                                  {meal.is_kid_friendly && (
                                    <Badge variant="outline" className="text-xs">
                                      <Baby className="w-3 h-3 mr-1" />
                                      Kid
                                    </Badge>
                                  )}
                                  {meal.is_quick && (
                                    <Badge variant="outline" className="text-xs">
                                      <Zap className="w-3 h-3 mr-1" />
                                      Quick
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteMeal(meal.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 text-nurture-muted hover:text-red-500"
                                data-testid={`delete-meal-${meal.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                            {meal.prep_notes && (
                              <p className="text-xs text-nurture-muted mt-2">{meal.prep_notes}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MealPlannerPage;
